import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ArrowLeft, Package } from "lucide-react";
import { load as loadCashfree } from "@cashfreepayments/cashfree-js";

export default function FrameDetail() {
  const { frameId } = useParams();
  const nav = useNavigate();
  const [frame, setFrame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ full_name: "", mobile: "", address: "" });
  const [cashfreeEnv, setCashfreeEnv] = useState("sandbox");
  const cashfreeRef = useRef(null);

  useEffect(() => {
    api.get(`/frames/${frameId}`)
      .then((r) => setFrame(r.data))
      .catch(() => nav("/frames"))
      .finally(() => setLoading(false));
    api.get("/config").then(async (r) => {
      const env = r.data?.cashfree_env === "production" ? "production" : "sandbox";
      setCashfreeEnv(env);
      try {
        cashfreeRef.current = await loadCashfree({ mode: env });
      } catch (e) {
        console.error("Cashfree SDK load failed", e);
      }
    }).catch(() => {});
  }, [frameId, nav]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const canCheckout =
    form.full_name.trim().length >= 2 &&
    form.mobile.trim().length >= 8 &&
    form.address.trim().length >= 6;

  const submit = async (e) => {
    e.preventDefault();
    if (!canCheckout) {
      toast.error("Please fill your full name, mobile number, and full address");
      return;
    }
    if (!cashfreeRef.current) {
      toast.error("Payment gateway is still loading. Please try again.");
      return;
    }
    setBusy(true);
    try {
      const { data: order } = await api.post("/frames/orders/create", {
        frame_id: frame.id,
        customer: {
          full_name: form.full_name.trim(),
          mobile: form.mobile.trim(),
          address: form.address.trim(),
        },
      });
      const result = await cashfreeRef.current.checkout({
        paymentSessionId: order.payment_session_id,
        redirectTarget: "_modal",
      });
      if (result?.error) {
        toast.error(result.error.message || "Payment cancelled");
        setBusy(false);
        return;
      }
      const { data: confirmed } = await api.post("/frames/orders/verify", {
        cf_order_id: order.cf_order_id,
        frame_id: frame.id,
        customer: {
          full_name: form.full_name.trim(),
          mobile: form.mobile.trim(),
          address: form.address.trim(),
        },
      });
      toast.success("Payment received — your frame is on its way!");
      nav(`/frames/confirmation/${confirmed.order_number}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-40 flex justify-center relative z-10">
        <Loader2 className="w-6 h-6 animate-spin text-gold" />
      </div>
    );
  }
  if (!frame) return null;

  return (
    <div className="pt-28 pb-20 px-6 lg:px-10 relative z-10">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => nav("/frames")}
          className="btn-ghost !py-1.5 !px-3 text-xs mb-8"
          data-testid="back-to-frames"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to all frames
        </button>

        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10">
          {/* Frame image */}
          <div className="glass rounded-3xl p-6 h-fit lg:sticky lg:top-24">
            <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-black/40">
              <img
                src={frame.image_url}
                alt={`Teuralink ${frame.planet} frame`}
                className="w-full h-full object-cover"
                data-testid="frame-hero-image"
              />
            </div>
            <div className="mt-5 text-xs text-white/50">
              Premium wooden frame · Museum-grade fine art print · Ships within 5–7 business days
            </div>
          </div>

          {/* Detail + form */}
          <form onSubmit={submit} className="glass rounded-3xl p-8 lg:p-10">
            <div className="overline text-gold">Framed Print</div>
            <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2" data-testid="frame-planet-title">
              {frame.planet}
            </h1>
            <p className="text-white/70 mt-4 italic">"{frame.quote}"</p>
            <div className="mt-6 flex items-center gap-4">
              <div className="font-mono-space text-3xl text-gold" data-testid="frame-price">₹{frame.price_inr}</div>
              <div className="text-xs text-white/45">Flat rate · Shipping included</div>
            </div>

            <div className="border-t border-white/10 mt-8 pt-8">
              <div className="overline text-gold">Shipping Details</div>
              <div className="mt-5 space-y-4">
                <Field
                  label="Full Name"
                  value={form.full_name}
                  onChange={set("full_name")}
                  testid="frame-name"
                  placeholder="As it should appear on the delivery"
                />
                <Field
                  label="Mobile Number"
                  value={form.mobile}
                  onChange={set("mobile")}
                  testid="frame-mobile"
                  placeholder="10-digit Indian mobile number"
                />
                <Field
                  label="Full Address"
                  value={form.address}
                  onChange={set("address")}
                  testid="frame-address"
                  area
                  placeholder="House / flat number, street, area, city, state, PIN"
                />
              </div>
            </div>

            <div className="mt-6 p-4 border border-white/10 rounded-xl text-xs text-white/60 flex gap-3">
              <ShieldCheck className="w-4 h-4 text-gold shrink-0 mt-0.5" />
              Your details are encrypted and only used to ship your frame.
            </div>

            <button
              type="submit"
              disabled={busy || !canCheckout}
              data-testid="frame-checkout"
              className="btn-primary mt-6 w-full justify-center"
              style={{ opacity: !canCheckout ? 0.45 : 1 }}
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              <Package className="w-4 h-4" />
              Pay ₹{frame.price_inr} & Place Order
            </button>
            {!canCheckout && (
              <p className="mt-3 text-[11px] text-white/50 text-center" data-testid="frame-checkout-hint">
                Fill your name, mobile number, and address to enable checkout.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, area, placeholder }) {
  return (
    <label className="block">
      <span className="overline block mb-2 text-white/60">{label}</span>
      {area ? (
        <textarea
          value={value}
          onChange={onChange}
          rows={3}
          placeholder={placeholder}
          data-testid={testid}
          className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40 focus:border-[#D4AF37]/40"
        />
      ) : (
        <input
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          data-testid={testid}
          className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40 focus:border-[#D4AF37]/40"
        />
      )}
    </label>
  );
}
